import React, { useState } from 'react';
import { ArrowRight, Search, ShieldCheck } from 'lucide-react';
import { MAIN_API_URL } from '../../configuration';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { activateImpersonationSession } from './impersonationSession';

const MONGO_ID_PATTERN = /^[a-f\d]{24}$/i;

export async function requestSupportAccess(storeId, {
  adminToken = window.localStorage.getItem('token'),
  fetchImpl = window.fetch,
} = {}) {
  if (!adminToken) throw new Error('Tu sesión administradora venció. Volvé a iniciar sesión.');

  const response = await fetchImpl(`${MAIN_API_URL}/admin/impersonation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-token': adminToken,
    },
    body: JSON.stringify({ storeId }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.msg || 'No se pudo crear el acceso de soporte');
  }
  return data;
}

function redirectTo(path) {
  window.location.replace(path);
}

export default function SupportStoreAccess({
  requestAccess = requestSupportAccess,
  activateSession = activateImpersonationSession,
  onSuccess = redirectTo,
}) {
  const [storeId, setStoreId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedStoreId = storeId.trim();
    if (!MONGO_ID_PATTERN.test(normalizedStoreId)) {
      setError('El _id debe tener exactamente 24 caracteres hexadecimales.');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const result = await requestAccess(normalizedStoreId);
      activateSession(result);
      onSuccess('/home');
    } catch (requestError) {
      setError(requestError.message || 'No se pudo abrir la tienda');
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-72px)] bg-background px-4 py-12 text-foreground sm:px-6 sm:py-16">
      <div className="mx-auto w-full max-w-[640px]">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-primary-border bg-primary-tint text-primary">
            <ShieldCheck aria-hidden="true" size={20} />
          </span>
          <div>
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-primary">Herramienta interna</p>
            <h1 className="m-0 mt-1 text-[28px] font-bold leading-tight tracking-[-0.02em] text-foreground">
              Acceso a una tienda
            </h1>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-5">
            <CardTitle className="text-xl">Buscar por _id de Mongo</CardTitle>
            <CardDescription className="max-w-[52ch] leading-relaxed">
              Genera una sesión temporal de 15 minutos para revisar la cuenta como soporte.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-5" onSubmit={handleSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="support-store-id">_id de Mongo</Label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={17} aria-hidden="true" />
                  <Input
                    id="support-store-id"
                    name="storeId"
                    value={storeId}
                    onChange={(event) => {
                      setStoreId(event.target.value);
                      if (error) setError('');
                    }}
                    placeholder="69b179dee433d84e6f526af1"
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck="false"
                    className="h-12 pl-10 font-mono text-[13px] tracking-[0.02em]"
                    aria-describedby="support-store-id-help"
                    aria-invalid={Boolean(error)}
                    autoFocus
                  />
                </div>
                <p id="support-store-id-help" className="m-0 text-xs leading-relaxed text-muted-foreground">
                  Pegá el identificador de 24 caracteres de la colección stores.
                </p>
              </div>

              {error ? (
                <Alert variant="danger">
                  <AlertTitle>No se pudo abrir la tienda</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <div className="rounded-md border border-border bg-secondary px-4 py-3 text-[13px] leading-relaxed text-muted-foreground">
                El acceso es de <strong className="font-semibold text-foreground">solo lectura</strong>. No permite cobrar, facturar ni modificar información.
              </div>

              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Abriendo tienda…' : 'Entrar a tienda'}
                {loading ? null : <ArrowRight aria-hidden="true" size={17} />}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mx-auto mt-4 max-w-[54ch] text-center text-xs leading-relaxed text-muted-foreground">
          Cada acceso queda identificado con la cuenta administradora que lo creó.
        </p>
      </div>
    </main>
  );
}
